import React, { useState, useEffect } from "react";
import { VerificationService } from "../services/verificationService";
import { Session } from "../auth/session";
import { RemoteLogger } from "../services/remoteLogger";
import { Header } from "./Header";
import { IoArrowBackOutline } from "react-icons/io5";

interface VerificationDetailViewProps {
  id: number;
  onBack: () => void;
  onLogout: () => void;
}

// ── helpers ──────────────────────────────────────────────────────────────────

const STATUS_MAP: Record<
  number | string,
  { label: string; dot: string; badge: string; border: string }
> = {
  1: {
    label: "Pending",
    dot: "bg-amber-400",
    badge: "text-amber-700 bg-amber-50",
    border: "border-amber-200",
  },
  pending: {
    label: "Pending",
    dot: "bg-amber-400",
    badge: "text-amber-700 bg-amber-50",
    border: "border-amber-200",
  },
  2: {
    label: "Verified",
    dot: "bg-emerald-400",
    badge: "text-emerald-700 bg-emerald-50",
    border: "border-emerald-200",
  },
  verified: {
    label: "Verified",
    dot: "bg-emerald-400",
    badge: "text-emerald-700 bg-emerald-50",
    border: "border-emerald-200",
  },
  3: {
    label: "Rejected",
    dot: "bg-red-400",
    badge: "text-red-700 bg-red-50",
    border: "border-red-200",
  },
  rejected: {
    label: "Rejected",
    dot: "bg-red-400",
    badge: "text-red-700 bg-red-50",
    border: "border-red-200",
  },
};

const getStatus = (s: any) =>
  STATUS_MAP[typeof s === "string" ? s.toLowerCase() : s] ?? {
    label: String(s),
    dot: "bg-gray-300",
    badge: "text-gray-600 bg-gray-50",
    border: "border-gray-200",
  };

const fmt = (iso: string) =>
  new Date(iso).toLocaleString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const initials = (first?: string | null, last?: string | null) =>
  [(first ?? "").charAt(0), (last ?? "").charAt(0)].filter(Boolean).join("").toUpperCase() || "?";

const fullName = (contact: any) =>
  [contact?.firstName, contact?.lastName].filter(Boolean).join(" ") ||
  contact?.email ||
  contact?.username ||
  "Unknown";

// ── sub-components ────────────────────────────────────────────────────────────

const Avatar = ({ contact, size = "md" }: { contact: any; size?: "sm" | "md" }) => {
  const s = size === "sm" ? "w-7 h-7 text-[10px]" : "w-9 h-9 text-xs";
  return (
    <div
      className={`${s} rounded-full bg-[#33ccbf]/15 flex items-center justify-center font-bold text-[#33ccbf] shrink-0`}
    >
      {initials(contact?.firstName, contact?.lastName)}
    </div>
  );
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 px-0.5">
    {children}
  </p>
);

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-white border border-gray-100 rounded-2xl overflow-hidden ${className}`}>
    {children}
  </div>
);

// ── main component ────────────────────────────────────────────────────────────

const VerificationDetailView: React.FC<VerificationDetailViewProps> = ({
  id,
  onBack,
  onLogout,
}) => {
  const [v, setV] = useState<any>(null);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const token = await Session.getToken();
        if (!token) {
          onLogout();
          return;
        }

        RemoteLogger.info(`[Verification] 🔍 Fetching detail for ID: ${id}`);
        const [response, auditRes] = await Promise.all([
          VerificationService.getById(id, token),
          VerificationService.getAuditLog(id, token),
        ]);
        RemoteLogger.info("[Verification] 📥 Detail received:", response);

        // Timeline is optional — the page still renders if the audit log fails
        const logs = (auditRes.data as any)?.data?.auditLogs;
        setAuditLogs(Array.isArray(logs) ? logs : []);

        if (response.data) {
          // envelope: response.data = { data: { success, data: {...} } }
          const envelope = response.data as any;
          const actual = envelope?.data ?? envelope;
          setV(actual);
        } else {
          // 403 here means "not allowed to view this one", not an expired session
          if (response.status === 401) {
            onLogout();
          } else setError(response.error || "Failed to load verification details");
        }
      } catch {
        setError("An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  if (loading)
    return (
      <div className="flex flex-col h-full bg-gray-50">
        <Header title="Details" />
        <button
          aria-label="Back"
          className="text-gray-500 hover:text-gray-700 px-4 mt-5"
          onClick={() => onBack()}
        >
          <IoArrowBackOutline size={16} />
        </button>
        <div className="flex flex-col items-center justify-center flex-1 gap-3 py-20">
          <div className="relative w-10 h-10">
            <div className="w-10 h-10 border-2 border-gray-100 rounded-full" />
            <div className="absolute inset-0 border-2 border-t-[#33ccbf] rounded-full animate-spin" />
          </div>
          <p className="text-xs text-gray-400 font-medium">Loading details...</p>
        </div>
      </div>
    );

  if (error || !v)
    return (
      <div className="flex flex-col h-full bg-gray-50">
        <Header title="Details" />
        <button
          aria-label="Back"
          className="text-gray-500 hover:text-gray-700 px-4 mt-5"
          onClick={() => onBack()}
        >
          <IoArrowBackOutline size={16} />
        </button>

        <div className="flex flex-col items-center justify-center flex-1 p-6 text-center">
          <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mb-3">
            <svg
              className="w-5 h-5 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
          </div>
          <p className="text-sm font-bold text-gray-800 mb-1">{error || "Something went wrong"}</p>
          <p className="text-xs text-gray-400 mb-4">Could not load verification details</p>
          <button onClick={onBack} className="text-xs font-bold text-[#33ccbf] hover:underline">
            ← Go back
          </button>
        </div>
      </div>
    );

  const status = getStatus(v.status);
  // The detail API no longer returns contact emails, so show the gettruID (username) instead
  const requester = v.requesterUser?.contact;
  const requesterHandle = v.requesterUser?.username;
  // A request can go to several verifiers; show every assignment
  const assignees: any[] = (v.assignments ?? []).filter((a: any) => a?.user);

  // responseValues sorted by displayOrder — these are the real submitted field values
  const fields: { label: string; value: string; key: string }[] = (v.responseValues ?? [])
    .filter((rv: any) => rv.value)
    .sort((a: any, b: any) => (a.inputField?.displayOrder ?? 0) - (b.inputField?.displayOrder ?? 0))
    .map((rv: any) => ({
      label: rv.templateInputField?.label ?? rv.inputField?.label ?? rv.inputField?.key ?? "Field",
      key: rv.inputField?.key ?? rv.templateInputField?.inputField?.key ?? String(rv.id),
      value: rv.value,
    }));

  // audit log events sorted oldest → newest (replaces the removed v.activities)
  const activities = [...auditLogs].sort(
    (a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
  const lastUpdated = activities.length
    ? activities[activities.length - 1].createdAt
    : v.updatedAt ?? v.createdAt;

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <Header title="Details" />
      <button
        aria-label="Back"
        className="text-gray-500 hover:text-gray-700 px-4 mt-5"
        onClick={() => onBack()}
      >
        <IoArrowBackOutline size={16} />
      </button>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* ── Status hero strip ── */}
        <Card>
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#33ccbf]/10 flex items-center justify-center shrink-0">
                <svg
                  className="w-5 h-5 text-[#33ccbf]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-400 mb-0.5">
                  {v.type ?? "Verification"} · #{v.id}
                </p>
                <p className="text-sm font-bold text-gray-900">
                  {v.organizationName ?? v.organization?.name ?? "Personal"}
                </p>
              </div>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full border ${status.badge} ${status.border}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
              {status.label}
            </span>
          </div>

          {/* Suspicious warning */}
          {v.isSuspicious && (
            <div className="mx-4 mb-4 flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
              <svg
                className="w-4 h-4 text-red-500 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
              <p className="text-[11px] font-semibold text-red-600">Flagged as suspicious</p>
            </div>
          )}
        </Card>

        {/* ── Submitted fields ── */}
        {fields.length > 0 && (
          <div>
            <SectionLabel>Submitted information</SectionLabel>
            <Card>
              {fields.map(({ label, key, value }, i) => (
                <div
                  key={key}
                  className={`px-4 py-3 flex items-start gap-3 ${i < fields.length - 1 ? "border-b border-gray-50" : ""}`}
                >
                  <div className="w-6 h-6 rounded-lg bg-[#33ccbf]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <svg
                      className="w-3 h-3 text-[#33ccbf]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-gray-400 mb-0.5">{label}</p>
                    <p className="text-[13px] font-semibold text-gray-800 break-all">{value}</p>
                  </div>
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* ── People ── */}
        <div>
          <SectionLabel>People</SectionLabel>
          <Card>
            {/* Requester */}
            <div className="px-4 py-3 flex items-center gap-3 border-b border-gray-50">
              <Avatar contact={requester} />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold text-gray-400 mb-0.5">Requested by</p>
                <p className="text-[13px] font-semibold text-gray-800 truncate">
                  {fullName(requester)}
                </p>
                {requesterHandle && (
                  <p className="text-[11px] text-gray-400 truncate">{requesterHandle}</p>
                )}
              </div>
            </div>

            {/* Assignees: one row each, with their own response status */}
            {assignees.map((a: any, i: number) => (
              <div
                key={a.id ?? i}
                className={`px-4 py-3 flex items-center gap-3 ${i < assignees.length - 1 ? "border-b border-gray-50" : ""}`}
              >
                <Avatar contact={a.user?.contact} />
                <div className="flex-1 min-w-0">
                  {i === 0 && (
                    <p className="text-[10px] font-semibold text-gray-400 mb-0.5">
                      Assigned to{assignees.length > 1 ? ` (${assignees.length})` : ""}
                    </p>
                  )}
                  <p className="text-[13px] font-semibold text-gray-800 truncate">
                    {a.user?.contact ? fullName(a.user.contact) : a.user?.username}
                  </p>
                  {a.user?.username && (
                    <p className="text-[11px] text-gray-400 truncate">{a.user.username}</p>
                  )}
                </div>
                {a.status !== undefined && a.status !== null && (
                  <span
                    className={`text-[9px] font-bold px-2 py-0.5 rounded-full shrink-0 border ${getStatus(a.status).badge} ${getStatus(a.status).border}`}
                  >
                    {getStatus(a.status).label}
                  </span>
                )}
              </div>
            ))}
          </Card>
        </div>

        {/* ── Activity timeline ── */}
        {activities.length > 0 && (
          <div>
            <SectionLabel>Activity</SectionLabel>
            <Card className="px-4 py-3">
              {activities.map((act: any, i: number) => {
                const actContact = act.performedByUser?.contact;
                const isLast = i === activities.length - 1;
                return (
                  <div key={act.id} className="flex gap-3">
                    {/* Timeline spine */}
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${isLast ? "bg-[#33ccbf]" : "bg-gray-200"}`}
                      />
                      {!isLast && <div className="w-px flex-1 bg-gray-100 my-1 min-h-[20px]" />}
                    </div>
                    <div className={`flex-1 min-w-0 ${!isLast ? "pb-3" : ""}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-[12px] font-semibold text-gray-800 capitalize">
                            {String(act.eventType ?? "").toLowerCase().replace(/_/g, " ")}
                          </p>
                          <p className="text-[11px] text-gray-400">
                            {actContact ? fullName(actContact) : act.performedByUser?.username}
                          </p>
                          {act.description && (
                            <p className="text-[11px] text-gray-500 mt-1">{act.description}</p>
                          )}
                        </div>
                        <p className="text-[10px] text-gray-400 whitespace-nowrap shrink-0 mt-0.5">
                          {fmt(act.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Card>
          </div>
        )}

        {/* ── Meta row ── */}
        <div className="grid grid-cols-2 gap-2">
          <Card className="px-3 py-3">
            <p className="text-[10px] font-semibold text-gray-400 mb-1">Created</p>
            <p className="text-[12px] font-semibold text-gray-800">{fmt(v.createdAt)}</p>
          </Card>
          <Card className="px-3 py-3">
            <p className="text-[10px] font-semibold text-gray-400 mb-1">Last updated</p>
            <p className="text-[12px] font-semibold text-gray-800">{fmt(lastUpdated)}</p>
          </Card>
        </div>

        <div className="h-2" />
      </div>
    </div>
  );
};

export default VerificationDetailView;
